/**
 * How a reversal outcome is reported (design D10).
 *
 * `reverse-grant` answers `200` with a positive `shortfall` when it could only
 * partially — or not at all — claw the balance back. A partial reversal
 * rendered as a plain success is how an operator comes to believe money was
 * returned when it was not, so both figures are always stated, and a shortfall
 * is never presented as a complete reversal.
 *
 * Amounts stay digit strings throughout: they are compared with `BigInt` and
 * interpolated as they arrived.
 */

export interface ReversalFigures {
  requested: string
  total_reversed: string
  shortfall: string
}

export type ReversalKind = 'full' | 'partial' | 'none'

export function classifyReversal(figures: ReversalFigures): ReversalKind {
  const shortfall = BigInt(figures.shortfall)
  if (shortfall === 0n) return 'full'
  return BigInt(figures.total_reversed) === 0n ? 'none' : 'partial'
}

/**
 * A message that always names both figures, in the virtual buckets' own atomic
 * unit — which is what the account-level path debits, and the ONLY path that
 * returns these two figures at all.
 */
export function describeReversal(figures: ReversalFigures): string {
  const kind = classifyReversal(figures)
  if (kind === 'full') {
    return `已全额回收 ${figures.total_reversed}（虚拟桶原子单位），未回收 0。`
  }
  if (kind === 'none') {
    return `未能回收任何余额：已回收 0，缺口 ${figures.shortfall}（虚拟桶原子单位）。`
  }
  return (
    `部分回收：已回收 ${figures.total_reversed}，缺口 ${figures.shortfall}` +
    `（虚拟桶原子单位，共请求 ${figures.requested}）。`
  )
}

// ---------------------------------------------------------------------------
// Path B: the directed monetary debit (grant-dialog-monetary-bonus-target, D7)
// ---------------------------------------------------------------------------

/** Where an `adjustment`/`debit` entry can be found, stated on success. */
export const DIRECTED_DEBIT_LEDGER_NOTE =
  '该笔回收在流水中是一条 adjustment / debit 条目：它不出现在「赠送」预设视图里，' +
  '请用「全部」视图或按 entry_type=adjustment、direction=debit 筛选查看。'

/**
 * The directed path is all-or-nothing and returns no `total_reversed` /
 * `shortfall`, so its success names exactly the bucket and the amount debited —
 * never a figure the server did not return.
 */
export function describeDirectedDebit(
  bucketLabel: string,
  renderedAmount: string
): string {
  return `已从 ${bucketLabel} 定向回收 ${renderedAmount}。${DIRECTED_DEBIT_LEDGER_NOTE}`
}

/**
 * A `422 insufficient_balance` answering a **first** submission: a determinate
 * refusal with nothing debited. It is deliberately NOT dressed up as a
 * shortfall or as `total_reversed = 0` — the server returned neither, and this
 * path does not reclaim partially.
 *
 * A `422` answering a retry after an unknown outcome never reaches this
 * function: `applyOutcome` gives the unknown-outcome retry rule precedence, so
 * the operator is told to check the ledger instead.
 */
export const DIRECTED_DEBIT_INSUFFICIENT_MESSAGE =
  '余额不足，未扣减任何余额：定向回收是全有或全无的，不做部分回收，也没有缺口可报。' +
  '如需回收较小的金额，请关闭本弹窗后重新发起。'
